import { useMemo, useState } from 'react';
import { Badge, Button, Panel, ScreenHeader, StateBox, Table, type Column } from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import type { AdminApi, WebhookDelivery } from '../data/types.js';
import { canReplay, isDeadLetter, webhookTone } from '../data/shape.js';
import { StatStrip, NameCell, dateTimeLabel } from '../components/AdminBits.js';

/**
 * Webhook ops (PRD §15, §20). Delivery status, replay and the dead-letter queue. A failed or
 * dead-lettered delivery can be replayed (re-queued); the dead-letter panel isolates
 * deliveries that exhausted their attempts so they are not lost. Replay is local to this
 * offline console session.
 */
export function Webhooks({ api }: { api: AdminApi }) {
  const { data, loading, error } = useAsync(() => api.getWebhooks());
  const [replayed, setReplayed] = useState<ReadonlySet<string>>(new Set());

  const rows = useMemo<WebhookDelivery[]>(
    () => (data ?? []).map((w) => (replayed.has(w.id) ? { ...w, status: 'pending', attempts: w.attempts + 1, response_code: null } : w)),
    [data, replayed],
  );

  if (loading) return <StateBox kind="loading" title="Loading webhook deliveries" />;
  if (error || !data) return <StateBox kind="error" title="Could not load webhook deliveries">{error}</StateBox>;

  const dead = rows.filter(isDeadLetter);

  const columns: Column<WebhookDelivery>[] = [
    { key: 'event', header: 'Event', render: (w) => <NameCell name={w.event} sub={w.id} /> },
    { key: 'org', header: 'Org', render: (w) => w.org_name },
    { key: 'endpoint', header: 'Endpoint', mono: true, render: (w) => w.endpoint.replace(/^https?:\/\//, '') },
    { key: 'status', header: 'Status', render: (w) => <Badge tone={webhookTone(w.status)} pulse={w.status === 'pending'}>{w.status.replace('_', '-')}</Badge> },
    {
      key: 'attempts',
      header: 'Attempts',
      mono: true,
      render: (w) => (
        <span style={{ color: w.attempts >= w.max_attempts ? 'var(--status-fail-text)' : 'var(--fg-2)' }}>
          {w.attempts}/{w.max_attempts}
        </span>
      ),
    },
    { key: 'code', header: 'Code', mono: true, render: (w) => (w.response_code === null ? '—' : String(w.response_code)) },
    { key: 'last', header: 'Last attempt', mono: true, render: (w) => dateTimeLabel(w.last_attempt_at) },
    {
      key: 'action',
      header: '',
      render: (w) =>
        replayed.has(w.id) ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--status-info-text)' }}>re-queued</span>
        ) : canReplay(w) ? (
          <Button sm variant="secondary" icon="refresh" onClick={() => setReplayed((p) => new Set(p).add(w.id))}>
            Replay
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="screen">
      <ScreenHeader eyebrow="Webhook ops" title="Delivery, replay & dead-letter" lead="Delivery status, replay and the dead-letter queue. A failed or dead-lettered delivery can be re-queued; the dead-letter panel isolates deliveries that exhausted their attempts so they are not lost." />
      <StatStrip
        stats={[
          { label: 'Deliveries', value: rows.length },
          { label: 'Delivered', value: rows.filter((w) => w.status === 'delivered').length, tone: 'pass' },
          { label: 'Failed', value: rows.filter((w) => w.status === 'failed').length, tone: 'warn' },
          { label: 'Dead-letter', value: dead.length, tone: dead.length > 0 ? 'fail' : 'default' },
        ]}
      />
      <Panel title="Webhook deliveries" right={<span className="metatext">status · replay · dead-letter</span>} flush bodyStyle={{ maxHeight: 520, overflow: 'auto' }}>
        <Table columns={columns} rows={rows} rowKey={(w) => w.id} />
      </Panel>
      {dead.length > 0 && (
        <Panel title="Dead-letter queue" right={<span className="metatext">attempts exhausted — replay to re-queue</span>} flush>
          <Table columns={columns} rows={dead} rowKey={(w) => w.id} />
        </Panel>
      )}
    </div>
  );
}
